import { Button, Typography, css } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ADD_BUTTON_COLUMN_WIDTH } from "../../appointment-schedule.constants.js";
import { hexToRgb } from "../../helpers/hexToRgb.js";
const Container = styled(Button, {
  shouldForwardProp: (props) => props !== "top" && props !== "height" && props !== "left" && props !== "width" && props !== "isTreated" && props !== "hasError" && props !== "isBlocker" && props !== "colorCode" && props !== "colorBackground" && props !== "isShort" && props !== "isDragging" && props !== "isResizing" && props !== "isSelected" && props !== "isCancelled" && props !== "isRiskyAppointment"
})(
  ({
    theme,
    top,
    left,
    width,
    height,
    colorBackground,
    isSelected,
    colorCode,
    isTreated,
    hasError,
    isBlocker,
    isCancelled,
    isShort,
    isDragging,
    isResizing,
    isRiskyAppointment
  }) => ({
    position: "absolute",
    minWidth: "auto",
    top: `${top ?? 0}%`,
    left: `${left ?? 0}%`,
    width: isDragging || isResizing ? `calc(99.9% + ${ADD_BUTTON_COLUMN_WIDTH}px)` : `calc(${width ?? 0}% - 2px)`,
    height: `calc(${height ?? 0}% - 4px)`,
    background: isSelected ? theme.palette.primary[25] : colorBackground ?? theme.palette.common.white,
    color: theme.palette.grey[800],
    zIndex: 2,
    padding: "0px 4px",
    borderRadius: "4px",
    boxSizing: "border-box",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    textAlign: "left",
    alignItems: "flex-start",
    border: `1px solid ${colorBackground ?? theme.palette.grey[300]}`,
    margin: "2px",
    ...colorCode && {
      borderLeftColor: colorCode,
      backgroundColor: `rgba(${hexToRgb(colorCode).r}, ${hexToRgb(colorCode).g}, ${hexToRgb(colorCode).b}, 0.1)`,
      "&:after": {
        content: '""',
        display: "block",
        position: "absolute",
        left: "-2px",
        height: "calc(100% + 4px)",
        top: "-2px",
        width: "3px",
        zIndex: 3,
        background: colorCode
      },
      "&:hover": {
        boxShadow: "none",
        backgroundColor: `rgba(${hexToRgb(colorCode).r}, ${hexToRgb(colorCode).g}, ${hexToRgb(colorCode).b}, 0.1)`
      }
    },
    "&:disabled": {
      opacity: 1,
      color: theme.palette.grey[800],
      background: isSelected ? theme.palette.primary[25] : colorBackground ?? theme.palette.common.white
    },
    ...isTreated && {
      "&&": {
        opacity: 0.7
      }
    },
    ...hasError && {
      "&&": {
        backgroundColor: theme.palette.error[100],
        borderColor: theme.palette.error[300],
        borderLeftColor: colorCode ?? theme.palette.error[100],
        "&:disabled": {
          color: theme.palette.grey[800],
          backgroundColor: theme.palette.error[100]
        },
        "&:hover": {
          backgroundColor: theme.palette.error[100],
          boxShadow: "none"
        }
      }
    },
    ...isBlocker && {
      "&&": {
        borderColor: theme.palette.error[300],
        opacity: isTreated ? 0.7 : 1,
        boxShadow: "none",
        backgroundImage: `repeating-linear-gradient(
        -45deg,
        ${theme.palette.error[50]},
        ${theme.palette.error[50]} 6px,
        ${colorBackground ?? theme.palette.common.white} 6px,
        ${colorBackground ?? theme.palette.common.white} 12px
      )`,
        "&:hover": {
          backgroundImage: `repeating-linear-gradient(
          -45deg,
          ${theme.palette.error[50]},
          ${theme.palette.error[50]} 6px,
          ${colorBackground ?? theme.palette.common.white} 6px,
          ${colorBackground ?? theme.palette.common.white} 12px
        )`
        }
      }
    },
    ...isCancelled && {
      "& *": {
        textDecoration: "line-through"
      }
    },
    ...isShort && {
      justifyContent: "center"
    },
    ...isSelected && {
      boxShadow: "0px 1px 3px 1px #00000026"
    },
    ...isRiskyAppointment && {
      "&&": {
        borderLeftColor: theme.palette.secondary[500],
        border: `2px solid ${theme.palette.secondary[500]}`
      }
    }
  })
);
const AppointmentDivider = styled(Typography, {
  shouldForwardProp: (props) => props !== "margin"
})(({ theme, margin }) => ({
  color: theme.palette.grey[700],
  ...margin && {
    margin: `0 ${margin}px`
  }
}));
const AppointmentTitle = styled(Typography, {
  shouldForwardProp: (props) => props !== "isShort" && props !== "isDragging" && props !== "isRiskyAppointment"
})(({ theme, isShort, isDragging, isRiskyAppointment }) => ({
  color: theme.palette.grey[700],
  whiteSpace: "nowrap",
  maxWidth: isShort ? isDragging ? "40px" : "60px" : "initial",
  overflow: "hidden",
  textOverflow: "ellipsis",
  width: "100%",
  flexShrink: 0,
  ...isRiskyAppointment && {
    display: "flex",
    alignItems: "center"
  }
}));
const PatientName = styled(Typography, {
  shouldForwardProp: (props) => props !== "isShort"
})(({ theme, isShort }) => ({
  whiteSpace: "nowrap",
  maxWidth: isShort ? "70px" : "initial",
  overflow: "hidden",
  textOverflow: "ellipsis",
  flexShrink: 0,
  "&:hover": {
    color: theme.palette.primary[800]
  }
}));
const BlockerTitle = styled(Typography, {
  shouldForwardProp: (props) => props !== "isExtended"
})`
  align-self: flex-end;
  text-align: right;
  text-transform: none;

  ${({ isExtended }) => isExtended && css`
      position: absolute;
      bottom: 0;
      right: 4px;
    `}
`;
const DetailsContainer = styled("div", {
  shouldForwardProp: (props) => props !== "isShort"
})`
  width: 100%;
  ${({ isShort }) => isShort && css`
      display: flex;
      align-items: center;

      & > *: {
        white-space: nowrap;
      }
    `}
`;
const TimeDetails = styled(Typography)`
  text-align: right;
  width: 100%;
`;
const ResizeHandler = styled("div")`
  position: absolute;
  width: 100%;
  height: 4px;
  left: 0;
  bottom: 0;
  cursor: row-resize;
  z-index: 3;
`;
export {
  AppointmentDivider,
  AppointmentTitle,
  BlockerTitle,
  Container,
  DetailsContainer,
  PatientName,
  ResizeHandler,
  TimeDetails
};
